import React from 'react';
import { PendingUser } from '@/schemas/admin.types';
import UserCard from '../../cards/UserCard';
import EmptyState from '../../ui/EmptyState';

interface PendingUsersCardListProps {
  users: PendingUser[];
  onApprove: (user: PendingUser) => Promise<void>;
  onReject: (user: PendingUser) => Promise<void>;
  isMobile: boolean;
  userVideoCountMap?: Map<string, number>;
}

/**
 * PendingUsersCardList - Mobile-optimized card list for pending users
 * Displays pending users in a card-based layout optimized for mobile viewing.
 */
const PendingUsersCardList: React.FC<PendingUsersCardListProps> = ({
  users,
  onApprove,
  onReject,
  isMobile,
  userVideoCountMap
}) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        <EmptyState 
          icon={
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          title="All caught up!"
          description="No pending user requests at the moment."
        />
      </div>
    );
  }

  return (
    <div className={`${isMobile ? 'space-y-1' : 'space-y-4'}`}>
      {/* Column Headers for pending users */}
      <div className="grid grid-cols-3 px-4 py-2 text-sm font-medium text-gray-500">
        <div className="text-left">Name</div>
        <div className="text-center">Role</div>
        <div className="text-center">Action</div>
      </div>
      
      {/* User Cards */}
      <div className="space-y-3">
        {users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            type="pending"
            onApprove={onApprove}
            onReject={onReject}
            videoCount={userVideoCountMap?.get(user.email) || 0}
          />
        ))}
      </div>
    </div>
  );
};

export default PendingUsersCardList;
